import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { BookOpen, CheckCircle2, Info, Lightbulb, ListOrdered, Target, X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../utils/tailwindUtils";
import { findReportGuide, getReportGuide } from "../../constants/reportGuides";

/*
 * "About this report" — what a report is for, how to read it and what each
 * number means. Content lives in constants/reportGuides.js, keyed by route, so
 * the header button finds it for whatever report is open without each report
 * page having to mount anything.
 */

const GuideSection = ({ icon: Icon, title, children }) => (
    <section className="space-y-2">
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Icon className="h-3.5 w-3.5" />
            {title}
        </h3>
        {children}
    </section>
);

export const ReportGuidePanel = ({ guide, open, onClose }) => {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!guide) return null;

    // Portalled to <body>: a report card scales on hover, and a transformed
    // ancestor would otherwise trap this `fixed` panel inside the card. Clicks
    // are stopped at the root because React still bubbles them through the
    // component tree to the card's navigate handler.
    return createPortal(
        <div onClick={(e) => e.stopPropagation()}>
            <div
                className={cn(
                    "fixed inset-0 z-50 bg-black/40 transition-opacity duration-200",
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={onClose}
            />
            <aside
                role="dialog"
                aria-label={`About ${guide.title}`}
                className={cn(
                    "fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-background shadow-xl border-l sm:max-w-md",
                    "transition-transform duration-200",
                    open ? "translate-x-0" : "translate-x-full"
                )}
            >
                <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
                    <div>
                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            About this report
                        </p>
                        <h2 className="text-lg font-semibold text-foreground">{guide.title}</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5 text-sm">
                    <GuideSection icon={Target} title="Purpose">
                        <p className="leading-relaxed text-foreground">{guide.purpose}</p>
                        {guide.whenToUse && (
                            <p className="leading-relaxed text-muted-foreground">
                                <span className="font-medium text-foreground">Use it when: </span>
                                {guide.whenToUse}
                            </p>
                        )}
                    </GuideSection>

                    {guide.howToUse?.length > 0 && (
                        <GuideSection icon={ListOrdered} title="How to use">
                            <ol className="list-decimal space-y-1.5 pl-5 leading-relaxed text-foreground">
                                {guide.howToUse.map((step, i) => <li key={i}>{step}</li>)}
                            </ol>
                        </GuideSection>
                    )}

                    {guide.whatYouGet?.length > 0 && (
                        <GuideSection icon={CheckCircle2} title="What you get">
                            <ul className="space-y-1.5 leading-relaxed text-foreground">
                                {guide.whatYouGet.map((item, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </GuideSection>
                    )}

                    {guide.terms?.length > 0 && (
                        <GuideSection icon={BookOpen} title="What the numbers mean">
                            <dl className="divide-y rounded-lg border">
                                {guide.terms.map(([term, meaning]) => (
                                    <div key={term} className="px-3 py-2">
                                        <dt className="font-medium text-foreground">{term}</dt>
                                        <dd className="text-muted-foreground leading-relaxed">{meaning}</dd>
                                    </div>
                                ))}
                            </dl>
                        </GuideSection>
                    )}

                    {guide.tips?.length > 0 && (
                        <GuideSection icon={Lightbulb} title="Good to know">
                            <ul className="space-y-1.5 rounded-lg bg-amber-50 p-3 leading-relaxed text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                                {guide.tips.map((tip, i) => <li key={i}>• {tip}</li>)}
                            </ul>
                        </GuideSection>
                    )}
                </div>
            </aside>
        </div>,
        document.body
    );
};

/** Header button — renders only when the current route has a guide. */
export const ReportGuideButton = () => {
    const { pathname } = useLocation();
    const guide = findReportGuide(pathname);
    const [open, setOpen] = useState(false);

    // Close on navigation so the panel never describes the previous report.
    useEffect(() => { setOpen(false); }, [pathname]);

    if (!guide) return null;
    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                className="h-8 gap-1.5 text-xs"
                title="What this report is for and how to read it"
            >
                <Info className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">About this report</span>
            </Button>
            <ReportGuidePanel guide={guide} open={open} onClose={() => setOpen(false)} />
        </>
    );
};

/** Small info icon for report cards on the index pages. */
export const ReportGuideIcon = ({ route, className }) => {
    const guide = getReportGuide(route);
    const [open, setOpen] = useState(false);
    if (!guide) return null;
    return (
        <>
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                className={cn(
                    "rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground",
                    className
                )}
                title="How to use this report"
                aria-label={`How to use ${guide.title}`}
            >
                <Info className="h-4 w-4" />
            </button>
            <ReportGuidePanel guide={guide} open={open} onClose={() => setOpen(false)} />
        </>
    );
};
